import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Row } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SmartLinkInfoBox } from '../smartLinkInfoBox/SmartLinkInfoBox';
import { SMLBioLinkBox } from '../smlBioLinkBox/SMLBioLinkBox';
import type { SMLBioLinkBoxProps } from '../smlBioLinkBox/props';
import { SMLCardGrid } from '../smlCardGrid/SMLCardGrid';
import { SMLFilterTabs } from '../smlFilterTabs/SMLFilterTabs';
import { SmartLinkFilter } from '../smlFilterTabs/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useSmartLinksGrid } from './useSmartLinksGrid';
import { SmartLinkClicks, SmartLinksCards, SmartLinksGridPage } from './SmartLinksGridCards';
import { SmartLinksGridFooter } from './SmartLinksGridFooter';

interface SmartLinksGridSectionProps {
  accountId: number;
  mainSmartLinkId: SMLBioLinkBoxProps['mainSmartLinkId'];
  tab: SmartLinkFilter;
  search: string;
  onTabChange: (tab: SmartLinkFilter) => void;
  clicksBySmartLink: SmartLinkClicks;
  onSmartLinkClick: (smartLinkId: number) => void;
}

/**
 * The tabs and the grid of every SmartLink of the tab (owner request 2026-10-19: no separate
 * "all" page). The caller keys this part by tab and search, so a change starts from the first
 * request again.
 */
const SmartLinksGridSectionComponent: React.FC<SmartLinksGridSectionProps> = ({
  accountId,
  mainSmartLinkId,
  tab,
  search,
  onTabChange,
  clicksBySmartLink,
  onSmartLinkClick,
}) => {
  const intl = useIntl();
  const grid = useSmartLinksGrid(accountId, tab, search);
  const morePages = useMemo(
    () => Array.from({ length: grid.pageCount - 1 }, (_, index) => index + 1),
    [grid.pageCount]
  );

  if (grid.firstPageError !== undefined) {
    return <ErrorView error={grid.firstPageError} retry={grid.retryFirstPage} />;
  }
  if (grid.firstPageLoading || !grid.firstPage) {
    return (
      <Row className="justify-content-center align-items-center h-100">
        <SmartLinkInfoBox
          text={intl.formatMessage({ id: 'app.smart-links.loading', defaultMessage: `Loading... Please wait` })}
        />
      </Row>
    );
  }

  const shown = grid.firstPage.totalItems;
  // The artist page is not in the list. It shows first on every tab, but not in a search result.
  const showsBioLink = search.trim() === '';

  return (
    <>
      <SMLFilterTabs value={tab} onChange={onTabChange} shown={shown} total={grid.totalOfAllTabs ?? shown} />
      {/* design: onboarding b3. The tour outlines the pages at the link stop. */}
      <div data-tour="link">
        <SMLCardGrid>
          {showsBioLink ? <SMLBioLinkBox accountId={accountId} mainSmartLinkId={mainSmartLinkId} /> : null}
          <SmartLinksCards
            smartLinks={grid.firstPage.results}
            clicksBySmartLink={clicksBySmartLink}
            onSmartLinkClick={onSmartLinkClick}
          />
          {morePages.map((page) => (
            <SmartLinksGridPage
              key={page}
              accountId={accountId}
              page={page}
              tab={tab}
              search={search}
              clicksBySmartLink={clicksBySmartLink}
              onSmartLinkClick={onSmartLinkClick}
            />
          ))}
        </SMLCardGrid>
      </div>
      <SmartLinksGridFooter
        hasMore={grid.hasMore}
        loadingMore={grid.loadingMore}
        moreError={grid.moreError}
        onShowMore={grid.showMore}
        onRetry={grid.retryMore}
        noResults={shown === 0 && search.trim() !== ''}
      />
    </>
  );
};

export const SmartLinksGridSection = TypedMemo(SmartLinksGridSectionComponent);
